'use client';
import React from 'react';
import styled from 'styled-components';
import { useLanguage } from '@/contexts/LanguageContext';
import { MenuBook as WikiIcon } from '@mui/icons-material';

const WikiContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    min-height: 100%;
    color: white;
    text-align: center;
    padding: 0px 20px 40px 20px;
    box-sizing: border-box;
    overflow-y: auto;
    gap: 60px;
`;

const TitleSection = styled.div`
    width: 100%;
    max-width: 1100px;
    text-align: left;
    margin-top: 10px;
    margin-bottom: 48px;
    display: flex;
    flex-direction: column;
`;

const MainTitle = styled.h1`
    font-size: 2.5rem;
    font-weight: 800;
    color: white;
    margin: 0;
    margin-bottom: 8px;
    letter-spacing: -1px;
    font-family: 'Chakra Petch', sans-serif;
    text-transform: uppercase;
    
    span {
        color: #7260ff;
        text-shadow: 0 0 10px rgba(114, 96, 255, 0.6);
    }
`;

const MainSubtitle = styled.p`
    font-size: 1rem;
    color: rgba(255, 255, 255, 0.5);
    margin: 0;
    font-weight: 400;
    letter-spacing: 1px;
`;

const IntroSection = styled.div`
    border-radius: 16px;
    padding: 0px;
    margin-bottom: 20px;
    flex-direction: column;
    gap: 20px;
`;

const IntroTitle = styled.h2`
    font-size: 1.8rem;
    font-weight: 800;
    margin-bottom: 15px;
    background: linear-gradient(90deg, #fff, #a5b4fc);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
`;

const IntroText = styled.p`
    font-size: 1rem;
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 20px;
    max-width: 800px;

    strong {
        color: #fff;
    }
`;

const StepList = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 20px;
`;

const StepCard = styled.div`
    //background: rgba(0, 0, 0, 0.2);
    //padding: 20px;
    border-radius: 12px;
    //border: 1px solid rgba(255, 255, 255, 0.05);
    display: flex;
    flex-direction: column;
    gap: 10px;
    
    h3 {
        color: #ec4899; // Using the pink accent
        font-size: 1.1rem;
        font-weight: 700;
        text-transform: uppercase;
    }

    p {
        font-size: 0.9rem;
        color: rgba(255, 255, 255, 0.6);
    }
`;

const ModuleSection = styled.section`
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 15px;
    margin-bottom: 25px;
    padding-bottom: 15px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);

    h2 {
        font-size: 1.5rem;
        font-weight: 700;
        color: #fff;
        text-transform: uppercase;
        letter-spacing: 1px;
    }
`;

const Grid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 20px;
`;

const CommandCard = styled.div`
    background: rgba(30, 41, 59, 0.4);
    border: 1px solid rgba(255, 255, 255, 0.05);
    border-radius: 12px;
    padding: 20px;
    transition: all 0.2s ease;
    display: flex;
    flex-direction: column;
    gap: 12px;
    height: 100%;

    &:hover {
        background: rgba(30, 41, 59, 0.6);
        border-color: rgba(255, 255, 255, 0.1);
        transform: translateY(-2px);
    }
`;

const CommandHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
`;

const CommandTitle = styled.code`
    font-family: 'Consolas', monospace;
    font-size: 0.95rem;
    font-weight: 700;
    color: #a5b4fc;
    background: rgba(99, 102, 241, 0.1);
    padding: 4px 8px;
    border-radius: 4px;
`;

const CommandDesc = styled.p`
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.7);
    line-height: 1.5;
`;

const WikiView = () => {
    const { t } = useLanguage();

    return (
        <WikiContainer>
            <TitleSection>
                <MainTitle><span>Wi</span>ki</MainTitle>
                <MainSubtitle>{t('WIKI_PAGE_SUBTITLE')}</MainSubtitle>
            </TitleSection>

            <IntroSection>
                <IntroTitle>{t('WIKI_WELCOME_TITLE')}</IntroTitle>
                <IntroText>
                    {t('WIKI_INTRO_P1')}<strong>{t('WIKI_INTRO_P2')}</strong>{t('WIKI_INTRO_P3')}
                </IntroText>

                <StepList>
                    <StepCard>
                        <h3>{t('WIKI_STEP_1_TITLE')}</h3>
                        <p>{t('WIKI_STEP_1_DESC')}</p>
                    </StepCard>
                    <StepCard>
                        <h3>{t('WIKI_STEP_2_TITLE')}</h3>
                        <p>{t('WIKI_STEP_2_DESC')}</p>
                    </StepCard>
                    <StepCard>
                        <h3>{t('WIKI_STEP_3_TITLE')}</h3>
                        <p>{t('WIKI_STEP_3_DESC')}</p>
                    </StepCard>
                </StepList>
            </IntroSection>

            {/* CROSSHAIR SECTION */}
            <ModuleSection>
                <SectionHeader>
                    <h2>{t('WIKI_SECTION_CROSSHAIR')}</h2>
                </SectionHeader>
                <Grid>
                    <CommandCard>
                        <CommandHeader><CommandTitle>cl_crosshairsize</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_CROSSHAIR_SIZE_DESC')}</CommandDesc>
                    </CommandCard>
                    <CommandCard>
                        <CommandHeader><CommandTitle>cl_crosshairgap</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_CROSSHAIR_GAP_DESC')}</CommandDesc>
                    </CommandCard>
                    <CommandCard>
                        <CommandHeader><CommandTitle>cl_crosshairthickness</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_CROSSHAIR_THICKNESS_DESC')}</CommandDesc>
                    </CommandCard>
                    <CommandCard>
                        <CommandHeader><CommandTitle>cl_crosshairstyle</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_CROSSHAIR_STYLE_DESC')}</CommandDesc>
                    </CommandCard>
                    <CommandCard>
                        <CommandHeader><CommandTitle>cl_crosshair_drawoutline</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_CROSSHAIR_DRAWOUTLINE_DESC')}</CommandDesc>
                    </CommandCard>
                    <CommandCard>
                        <CommandHeader><CommandTitle>cl_crosshair_outlinethickness</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_CROSSHAIR_OUTLINETHICKNESS_DESC')}</CommandDesc>
                    </CommandCard>
                    <CommandCard>
                        <CommandHeader><CommandTitle>cl_crosshaircolor</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_CROSSHAIR_COLOR_DESC')}</CommandDesc>
                    </CommandCard>
                    <CommandCard>
                        <CommandHeader><CommandTitle>cl_crosshaircolor_r/g/b</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_CROSSHAIR_RGB_DESC')}</CommandDesc>
                    </CommandCard>
                    <CommandCard>
                        <CommandHeader><CommandTitle>cl_crosshairalpha</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_CROSSHAIR_ALPHA_DESC')}</CommandDesc>
                    </CommandCard>
                    <CommandCard>
                        <CommandHeader><CommandTitle>cl_crosshairdot</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_CROSSHAIR_DOT_DESC')}</CommandDesc>
                    </CommandCard>
                    <CommandCard>
                        <CommandHeader><CommandTitle>cl_crosshair_t</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_CROSSHAIR_T_DESC')}</CommandDesc>
                    </CommandCard>
                    <CommandCard>
                        <CommandHeader><CommandTitle>cl_crosshair_recoil</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_CROSSHAIR_RECOIL_DESC')}</CommandDesc>
                    </CommandCard>
                    <CommandCard>
                        <CommandHeader><CommandTitle>cl_crosshair_sniper_width</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_CROSSHAIR_SNIPER_WIDTH_DESC')}</CommandDesc>
                    </CommandCard>
                    <CommandCard>
                        <CommandHeader><CommandTitle>cl_crosshair_friendly_warning</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_CROSSHAIR_FRIENDLY_WARNING_DESC')}</CommandDesc>
                    </CommandCard>
                </Grid>
            </ModuleSection>

            {/* VIEWMODEL SECTION */}
            <ModuleSection>
                <SectionHeader>
                    <h2>{t('WIKI_SECTION_VIEWMODEL')}</h2>
                </SectionHeader>
                <Grid>
                    <CommandCard>
                        <CommandHeader><CommandTitle>viewmodel_fov</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_VIEWMODEL_FOV_DESC')}</CommandDesc>
                    </CommandCard>
                    <CommandCard>
                        <CommandHeader><CommandTitle>viewmodel_offset_x</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_VIEWMODEL_X_DESC')}</CommandDesc>
                    </CommandCard>
                    <CommandCard>
                        <CommandHeader><CommandTitle>viewmodel_offset_y</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_VIEWMODEL_Y_DESC')}</CommandDesc>
                    </CommandCard>
                    <CommandCard>
                        <CommandHeader><CommandTitle>viewmodel_offset_z</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_VIEWMODEL_Z_DESC')}</CommandDesc>
                    </CommandCard>
                    <CommandCard>
                        <CommandHeader><CommandTitle>cl_prefer_lefthanded</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_VIEWMODEL_LEFTHANDED_DESC')}</CommandDesc>
                    </CommandCard>
                </Grid>
            </ModuleSection>

            {/* MOUSE SECTION */}
            <ModuleSection>
                <SectionHeader>
                    <h2>{t('WIKI_SECTION_MOUSE')}</h2>
                </SectionHeader>
                <Grid>
                    <CommandCard>
                        <CommandHeader><CommandTitle>sensitivity</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_MOUSE_SENSITIVITY_DESC')}</CommandDesc>
                    </CommandCard>
                    <CommandCard>
                        <CommandHeader><CommandTitle>zoom_sensitivity_ratio</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_MOUSE_ZOOM_SENS_DESC')}</CommandDesc>
                    </CommandCard>
                    <CommandCard>
                        <CommandHeader><CommandTitle>m_yaw / m_pitch</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_MOUSE_YAW_PITCH_DESC')}</CommandDesc>
                    </CommandCard>
                    <CommandCard>
                        <CommandHeader><CommandTitle>m_rawinput</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_MOUSE_RAWINPUT_DESC')}</CommandDesc>
                    </CommandCard>
                </Grid>
            </ModuleSection>

            {/* HUD & RADAR SECTION */}
            <ModuleSection>
                <SectionHeader>
                    <h2>{t('WIKI_SECTION_HUD')}</h2>
                </SectionHeader>
                <Grid>
                    <CommandCard>
                        <CommandHeader><CommandTitle>hud_scaling</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_HUD_SCALING_DESC')}</CommandDesc>
                    </CommandCard>
                    <CommandCard>
                        <CommandHeader><CommandTitle>cl_hud_color</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_HUD_COLOR_DESC')}</CommandDesc>
                    </CommandCard>
                    <CommandCard>
                        <CommandHeader><CommandTitle>cl_radar_scale</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_RADAR_SCALE_DESC')}</CommandDesc>
                    </CommandCard>
                    <CommandCard>
                        <CommandHeader><CommandTitle>cl_hud_radar_scale</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_HUD_RADAR_SCALE_DESC')}</CommandDesc>
                    </CommandCard>
                    <CommandCard>
                        <CommandHeader><CommandTitle>cl_radar_always_centered</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_RADAR_CENTERED_DESC')}</CommandDesc>
                    </CommandCard>
                    <CommandCard>
                        <CommandHeader><CommandTitle>cl_radar_rotate</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_RADAR_ROTATE_DESC')}</CommandDesc>
                    </CommandCard>
                    <CommandCard>
                        <CommandHeader><CommandTitle>cl_showloadout</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_SHOWLOADOUT_DESC')}</CommandDesc>
                    </CommandCard>
                    <CommandCard>
                        <CommandHeader><CommandTitle>cl_radar_icon_scale_min</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_RADAR_ICON_SCALE_DESC')}</CommandDesc>
                    </CommandCard>
                    <CommandCard>
                        <CommandHeader><CommandTitle>cl_teammate_colors_show</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_TEAMMATE_COLORS_DESC')}</CommandDesc>
                    </CommandCard>
                </Grid>
            </ModuleSection>


            {/* GRAPHICS SECTION */}
            <ModuleSection>
                <SectionHeader>
                    <h2>{t('WIKI_SECTION_GRAPHICS')}</h2>
                </SectionHeader>
                <Grid>
                    <CommandCard>
                        <CommandHeader><CommandTitle>fps_max</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_FPS_MAX_DESC')}</CommandDesc>
                    </CommandCard>
                    <CommandCard>
                        <CommandHeader><CommandTitle>r_drawtracers_firstperson</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_TRACERS_DESC')}</CommandDesc>
                    </CommandCard>
                    <CommandCard>
                        <CommandHeader><CommandTitle>r_dynamic</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_DYNAMIC_DESC')}</CommandDesc>
                    </CommandCard>
                    <CommandCard>
                        <CommandHeader><CommandTitle>cl_allow_animated_avatars</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_ANIMATED_AVATARS_DESC')}</CommandDesc>
                    </CommandCard>
                    <CommandCard>
                        <CommandHeader><CommandTitle>cl_ragdoll_workaround_threshold</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_RAGDOLL_DESC')}</CommandDesc>
                    </CommandCard>
                    <CommandCard>
                        <CommandHeader><CommandTitle>engine_low_latency_sleep_after_client_tick</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_LOW_LATENCY_DESC')}</CommandDesc>
                    </CommandCard>
                </Grid>
            </ModuleSection>

            {/* PRACTICE SECTION */}
            <ModuleSection>
                <SectionHeader>
                    <h2>{t('WIKI_SECTION_PRACTICE')}</h2>
                    <span style={{ fontSize: '0.8rem', color: '#ec4899', fontWeight: 600, marginLeft: 'auto' }}>{t('WIKI_PRACTICE_WARNING')}</span>
                </SectionHeader>
                <Grid>
                    <CommandCard>
                        <CommandHeader><CommandTitle>sv_cheats</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_SV_CHEATS_DESC')}</CommandDesc>
                    </CommandCard>
                    <CommandCard>
                        <CommandHeader><CommandTitle>noclip</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_NOCLIP_DESC')}</CommandDesc>
                    </CommandCard>
                    <CommandCard>
                        <CommandHeader><CommandTitle>sv_rethrow_last_grenade</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_RETHROW_GRENADE_DESC')}</CommandDesc>
                    </CommandCard>
                    <CommandCard>
                        <CommandHeader><CommandTitle>sv_infinite_ammo</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_INFINITE_AMMO_DESC')}</CommandDesc>
                    </CommandCard>
                    <CommandCard>
                        <CommandHeader><CommandTitle>sv_grenade_trajectory_prac_pipreview</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_GRENADE_TRAJECTORY_DESC')}</CommandDesc>
                    </CommandCard>
                    <CommandCard>
                        <CommandHeader><CommandTitle>mp_warmup_end</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_WARMUP_END_DESC')}</CommandDesc>
                    </CommandCard>
                    <CommandCard>
                        <CommandHeader><CommandTitle>mp_restartgame</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_RESTART_GAME_DESC')}</CommandDesc>
                    </CommandCard>
                    <CommandCard>
                        <CommandHeader><CommandTitle>bot_kick</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_BOT_KICK_DESC')}</CommandDesc>
                    </CommandCard>
                    <CommandCard>
                        <CommandHeader><CommandTitle>bot_add_t / bot_add_ct</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_BOT_ADD_DESC')}</CommandDesc>
                    </CommandCard>
                    <CommandCard>
                        <CommandHeader><CommandTitle>bot_stop</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_BOT_STOP_DESC')}</CommandDesc>
                    </CommandCard>
                    <CommandCard>
                        <CommandHeader><CommandTitle>bot_place</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_BOT_PLACE_DESC')}</CommandDesc>
                    </CommandCard>
                </Grid>
            </ModuleSection>

            {/* LAUNCH OPTIONS SECTION */}
            <ModuleSection>
                <SectionHeader>
                    <h2>{t('WIKI_SECTION_LAUNCH')}</h2>
                </SectionHeader>
                <Grid>
                    <CommandCard>
                        <CommandHeader><CommandTitle>+exec autoexec.cfg</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_EXEC_AUTOEXEC_DESC')}</CommandDesc>
                    </CommandCard>
                    <CommandCard>
                        <CommandHeader><CommandTitle>-novid</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_NOVID_DESC')}</CommandDesc>
                    </CommandCard>
                    <CommandCard>
                        <CommandHeader><CommandTitle>-high</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_HIGH_DESC')}</CommandDesc>
                    </CommandCard>
                    <CommandCard>
                        <CommandHeader><CommandTitle>-freq / -refresh</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_FREQ_DESC')}</CommandDesc>
                    </CommandCard>
                    <CommandCard>
                        <CommandHeader><CommandTitle>-console</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_CONSOLE_DESC')}</CommandDesc>
                    </CommandCard>
                    <CommandCard>
                        <CommandHeader><CommandTitle>-fullscreen</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_FULLSCREEN_DESC')}</CommandDesc>
                    </CommandCard>
                    <CommandCard>
                        <CommandHeader><CommandTitle>-w / -h</CommandTitle></CommandHeader>
                        <CommandDesc>{t('WIKI_RESOLUTION_DESC')}</CommandDesc>
                    </CommandCard>
                </Grid>
            </ModuleSection>
        </WikiContainer>
    );
};

export default WikiView;
